import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import api from '@/lib/api';
import { FileUpload } from '@/components/ui/file-upload';
import { API_URL } from './CampaignConstants';

export const GalleryEditor = ({ campaignId, slug }) => {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', image_url: '', category: 'general' });

  const { data: gallery = [] } = useQuery({
    queryKey: ['campaign-gallery-admin', slug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/campaigns/${slug}/gallery`);
      return res.json();
    },
    enabled: !!slug
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const formData = new FormData();
      Object.entries(data).forEach(([k, v]) => formData.append(k, v ?? ''));
      return api.post(`/campaigns/admin/${campaignId}/gallery`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      toast.success('Image added');
      queryClient.invalidateQueries({ queryKey: ['campaign-gallery-admin', slug] });
      setShowAdd(false);
      setForm({ title: '', image_url: '', category: 'general' });
    },
    onError: (e) => {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : Array.isArray(d) ? d.map(x => x?.msg || JSON.stringify(x)).join('; ') : (e?.message || 'Failed to add image');
      toast.error(msg);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => api.delete(`/campaigns/admin/${campaignId}/gallery/${id}`),
    onSuccess: () => {
      toast.success('Image deleted');
      queryClient.invalidateQueries({ queryKey: ['campaign-gallery-admin', slug] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e?.message || 'Delete failed'),
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Photo Gallery</CardTitle>
          <CardDescription>Upload images to showcase your project</CardDescription>
        </div>
        <Button size="sm" onClick={() => setShowAdd(true)}>
          <Plus className="h-4 w-4 mr-1" />
          Add Image
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {gallery.map((img) => {
            const url = img.image_url || '';
            const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
            const isVideo = /\.(mp4|webm|mov|mkv)$/i.test(url);
            return (
              <div key={img.id} className="relative group">
                {isVideo ? (
                  <video
                    src={fullUrl}
                    muted
                    playsInline
                    controls
                    className="aspect-square object-cover rounded-lg w-full bg-black"
                  />
                ) : (
                  <img
                    src={fullUrl}
                    alt={img.title}
                    className="aspect-square object-cover rounded-lg w-full"
                  />
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => deleteMutation.mutate(img.id)}
                    data-testid={`gallery-delete-${img.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-slate-600 mt-1 truncate">{img.title}</p>
              </div>
            );
          })}
        </div>

        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Gallery Image/Video</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Image/video title"
                />
              </div>
              <div>
                <Label>Upload File</Label>
                <FileUpload
                  value={form.image_url}
                  onChange={(url) => setForm({ ...form, image_url: url })}
                  accept="image/*,video/*"
                  placeholder="Upload image or video"
                  previewHeight="180px"
                  fileType="gallery"
                  maxSize={100}
                  uploadEndpoint="/api/campaigns/admin/upload"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Input
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="Construction, Foundation, etc."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={() => createMutation.mutate(form)}>Add</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
